import {
  createContext,
  useContext,
  useEffect,
  Dispatch,
  SetStateAction,
  useState,
  FC,
  PropsWithChildren,
} from 'react';
import { ApolloError } from '@apollo/client';
import { useApolloContext } from 'contexts';
import { useAccessToken } from '@robinpowered/dashboard-apps-common';
import { config } from 'config';
import { Sentry } from 'lib/sentry';
import {
  GetCurrentUserAndOrgsForDashboardShellQuery,
  useGetCurrentUserAndOrgsForDashboardShellQuery,
  useGetCurrentUserDefaultRolesForTenantDashboardShellQuery,
  UserDefaultRoleSlug,
} from 'generated/graphql';
import {
  DEFAULT_PERMISSIONS,
  UseUserPermissions,
  useUserPermissions,
} from 'hooks/useUserPermissions';
import { getWorkspaceOrgSlugFromPathname } from 'utils/workspaceOrgSlugFromPath';

type GetCurrentUserAndOrgs = NonNullable<
  GetCurrentUserAndOrgsForDashboardShellQuery['getCurrentUserAndOrgs']
>;
export type Organization = NonNullable<
  GetCurrentUserAndOrgs['organizationBySlug']
>;
export type User = GetCurrentUserAndOrgs['user'];

type AuthContextValue = {
  loading: boolean;
  isLoggedIn: boolean;
  error: undefined | ApolloError;
  isUnauthenticated: boolean | undefined;
  currentUser: null | User;
  currentOrg: null | Organization;
  accessToken: string | null;
  belongsToMultipleOrgs: boolean;
  setOrgSlug: Dispatch<SetStateAction<string | undefined>>;
  orgSlug?: string;
  refetch?: () => void;
  currentOrgHasVMLicenses: boolean;
  userRoles: UserDefaultRoleSlug[] | undefined;
  userPermissions: UseUserPermissions;
};

const AuthContext = createContext<AuthContextValue>({
  loading: true,
  isLoggedIn: false,
  error: undefined,
  isUnauthenticated: false,
  currentUser: null,
  currentOrg: null,
  accessToken: null,
  belongsToMultipleOrgs: false,
  setOrgSlug: () => {},
  orgSlug: undefined,
  refetch: () => null,
  currentOrgHasVMLicenses: false,
  userRoles: undefined,
  userPermissions: {
    ...DEFAULT_PERMISSIONS,
    isLoading: false,
  },
});

/**
 * Extract initial org slug from the URL so the first query can include
 * organizationBySlug without waiting for a React render cycle.
 */
const getInitialOrgSlugFromUrl = (): string | undefined =>
  getWorkspaceOrgSlugFromPathname(window.location.pathname);

export const AuthContextProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { tenantId, setTenantId } = useApolloContext();
  const [orgSlug, setOrgSlug] = useState<string | undefined>(
    getInitialOrgSlugFromUrl
  );
  const { accessToken } = useAccessToken(config.sessionCookieName);

  const {
    data,
    loading: authLoading,
    error: authError,
    refetch,
  } = useGetCurrentUserAndOrgsForDashboardShellQuery({
    skip: !accessToken || !orgSlug,
    variables: {
      orgSlug: orgSlug ?? '',
      hasOrgSlug: !!orgSlug,
    },
  });

  const currentOrg = data?.getCurrentUserAndOrgs?.organizationBySlug ?? null;

  const belongsToMultipleOrgs =
    data?.getCurrentUserAndOrgs?.belongsToMultipleOrgs ?? false;

  // Tell ApolloContext what the current org ID is once we've logged in.
  useEffect(() => {
    if (currentOrg) {
      setTenantId(currentOrg.id);
      Sentry.setContext('organization', {
        id: currentOrg.id,
        slug: currentOrg.slug,
      });
    }
  }, [currentOrg, setTenantId]);

  useEffect(() => {
    if (data) {
      Sentry.setUser({
        id: data.getCurrentUserAndOrgs?.user?.id,
      });
    }
  }, [data]);

  const isUnauthenticated =
    authError &&
    // TODO: replace with error link like office app
    [
      'provide authorization',
      'not authenticated',
      'check your credentials',
    ].includes(authError.message.toLowerCase());

  const currentOrgHasVMLicenses =
    currentOrg?.features?.limits.visitorManagementLocations
      .overProvisioningLimit !== 0;

  /**
   * Show the loading state while:
   * 1. The query to get both the current user/orgs is in flight, or:
   * 2. The above query has succeeded but ApolloContext hasn't re-created
   *    a client yet with the tenantId header.
   *
   * Note: We don't wait indefinitely for accessToken. If authLoading is false
   * (query finished or was skipped) and we have no data, we stop loading.
   * This prevents infinite loading when the cookie doesn't exist.
   */
  const loading = authLoading || (!authLoading && !!currentOrg && !tenantId);

  /**
   * User is logged in if:
   * 1. Not loading
   * 2. No auth error
   * 3. Has data (user/orgs successfully fetched)
   *
   * If the query was skipped (no access token), data will be undefined,
   * so isLoggedIn will be false.
   */
  const isLoggedIn = !loading && !isUnauthenticated && !!data;
  const { data: userRolesData } =
    useGetCurrentUserDefaultRolesForTenantDashboardShellQuery({
      skip: !tenantId,
    });

  const userRoles =
    userRolesData?.getCurrentUserAndOrgs
      ?.userDefaultRoleSlugsForCurrentTenant ?? [];

  const userPermissions = useUserPermissions(
    currentOrg?.id ?? null,
    !!tenantId
  );

  const value: AuthContextValue = {
    loading: loading,
    isLoggedIn,
    error: authError,
    isUnauthenticated,
    userRoles,
    currentUser: data?.getCurrentUserAndOrgs?.user ?? null,
    accessToken: accessToken ?? null,
    belongsToMultipleOrgs,
    currentOrg,
    orgSlug,
    setOrgSlug,
    refetch,
    currentOrgHasVMLicenses,
    userPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  return useContext(AuthContext);
};
